import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import * as XLSX from "xlsx";
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from "../../components/ui/Loader";
import { useAuth } from '../../components/shared/AuthContext';

const Reports = () => {
  const { roleName, data } = useAuth();
  const [selectedColumn, setSelectedColumn] = useState(null);
  console.log("User details:", { roleName, branchId: data?.branchId });

  // Function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Determine if user can view all branches or just their own
  const isSuperAdmin = roleName === "superadmin";

  // Get user's branchId - only apply branch filtering for non-superadmin users
  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  // Set default dates to today
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [transactions, setTransactions] = useState([]);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState("");
  const [officerCustomers, setOfficerCustomers] = useState([]);
  const [branchManagers, setBranchManagers] = useState([]);
  const [selectedBranchManager, setSelectedBranchManager] = useState("");

  const [loading, setLoading] = useState(false);

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // With server-side pagination the backend already slices the data
  const currentRows = transactions;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage; // for S.No display

  // Cache references for API responses to avoid redundant fetching
  const staffDataCache = useRef(null);

  // Ref so pagination handlers can read latest page/limit without stale closure
  const pageRef = React.useRef(currentPage);
  const limitRef = React.useRef(rowsPerPage);
  React.useEffect(() => { pageRef.current = currentPage; }, [currentPage]);
  React.useEffect(() => { limitRef.current = rowsPerPage; }, [rowsPerPage]);

  const handleRowsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    setRowsPerPage(newLimit);
    setCurrentPage(1);
    limitRef.current = newLimit;
    doFetch(1, newLimit);
  };

  const exportToExcel = () => {
    const worksheetData = [
      [
        "S.No",
        "Date",
        "Customer ID",
        "Customer Name",
        "Account Number",
        "Transaction Type",
        "TransactionId",
        "Amount",
        "Narration",
        "Closing Balance",
        "Staff Name",
      ],
      ...transactions.map((tx, index) => [
        index + 1,
        formatDate(tx.date),
        tx.customerId,
        tx.customerName,
        tx.accountNumber,
        tx.transactionType,
        tx.transactionId,
        tx.amount,
        tx.narration,
        tx.closingBalance,
        tx.staffName || 'N/A',
      ]),
      [],
      ["Total Deposits:", "", "", "", totalDeposits],
      ["Total Withdrawals:", "", "", "", totalWithdrawals],
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c });
      if (!worksheet[headerCell]) continue;
      worksheet[headerCell].s = { font: { bold: true } };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction Report");
    XLSX.writeFile(
      workbook,
      `Transaction_Report_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch branches and admins on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        let branchesUrl = `${API_BASE_URL}/api/getModelData/branch_details`;
        let staffUrl = `${API_BASE_URL}/api/getModelData/staff_details`;

        if (shouldFilterByBranch) {
          branchesUrl += `?filter={"id":${userBranchId}}`;
          staffUrl += `?filter={"branchId":${userBranchId}}`;
          console.log(`Filtering by branch ID ${userBranchId} for ${roleName}`);
        }

        const [branchesResponse, staffResponse] = await Promise.all([
          axios.get(branchesUrl),
          axios.get(staffUrl),
        ]);

        let branchesData = branchesResponse.data.data || [];
        let staffData = Array.isArray(staffResponse.data.data) ? staffResponse.data.data : [];

        if (shouldFilterByBranch) {
          branchesData = branchesData.filter(branch =>
            String(branch.id) === String(userBranchId)
          );
          staffData = staffData.filter(staff =>
            String(staff.branchId) === String(userBranchId)
          );
        }

        setBranches(branchesData);
        staffDataCache.current = staffData;

        if (shouldFilterByBranch && branchesData.length > 0) {
          const branchId = String(userBranchId);
          setSelectedBranch(branchId);

          // Include Admin(2), Field Officer(3), Data Entry(4), Manager(5)
          const relevantRoles = [2, 3, 4, 5];
          const branchFieldOfficers = staffData.filter(
            (staff) => Number(staff.branchId) === Number(branchId) && relevantRoles.includes(Number(staff.roleId))
          );
          setFieldOfficers(branchFieldOfficers);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [shouldFilterByBranch, userBranchId, roleName]);

  // Handle branch selection
  const handleBranchChange = async (e) => {
    const branchId = e.target.value;
    setSelectedBranch(branchId);
    setSelectedFieldOfficer("");
    setSelectedBranchManager("");
    setOfficerCustomers([]);
    setCurrentPage(1);

    try {
      const staffResponse = await axios.get(
        `${API_BASE_URL}/api/getModelData/staff_details`
      );
      const allStaff = Array.isArray(staffResponse.data.data) ? staffResponse.data.data : [];
      console.log("All Staff for Branch:", allStaff);

      // Include Admin(2), Field Officer(3), Data Entry(4), Manager(5)
      const relevantRoles = [2, 3, 4, 5];
      const filteredFieldOfficers = allStaff.filter(
        (staff) => Number(staff.branchId) === Number(branchId) && relevantRoles.includes(Number(staff.roleId))
      );
      console.log("Filtered Staff (Admin, FO, DEO, Manager):", filteredFieldOfficers);
      setFieldOfficers(filteredFieldOfficers);

      const filteredManagers = allStaff.filter(
        (staff) => Number(staff.branchId) === Number(branchId) && Number(staff.roleId) === 5
      );
      console.log("Filtered Branch Managers:", filteredManagers);
      setBranchManagers(filteredManagers);

      if (filteredFieldOfficers.length > 0) {
        const fieldOfficerIds = filteredFieldOfficers.map((officer) => officer.id);
        console.log("Field Officer IDs:", fieldOfficerIds);

        const customerResponse = await axios.get(
          `${API_BASE_URL}/api/getModelData/personal_details`,
          {
            params: {
              filter: JSON.stringify({ fieldOfficerId: fieldOfficerIds }),
              includeDepth: 1,
            },
          }
        );
        console.log("Customer Response:", customerResponse.data);

        const customerData = Array.isArray(customerResponse.data.data)
          ? customerResponse.data.data
          : [];
        const customerIds = customerData.map((customer) => customer.customerUniqueId);
        console.log("Customers for all Field Officers in the Branch:", customerIds);
        setOfficerCustomers(customerIds);
      }
    } catch (error) {
      console.error("Error fetching staff or customers:", error);
      setFieldOfficers([]);
      setBranchManagers([]);
      setOfficerCustomers([]);
    }
  };

  // Handle field officer selection
  const handleFieldOfficerChange = async (e) => {
    const officerId = e.target.value;
    setSelectedFieldOfficer(officerId);
    setCurrentPage(1);

    if (!officerId) {
      setOfficerCustomers([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/getModelData/personal_details`,
        {
          params: {
            filter: JSON.stringify({ fieldOfficerId: Number(officerId) }),
            includeDepth: 1,
          },
        }
      );

      const customerData = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      const customerIds = customerData.map((customer) => customer.customerUniqueId);
      console.log("Customers for Selected Field Officer:", customerIds);
      setOfficerCustomers(customerIds);
    } catch (error) {
      console.error("Error fetching customers for field officer:", error);
      setOfficerCustomers([]);
    }
  };

  // Handle branch manager selection
  const handleBranchManagerChange = (e) => {
    setSelectedBranchManager(e.target.value);
    setCurrentPage(1);
  };

  // ── Core fetch (imperative — called by useEffect and pagination buttons) ──────
  const doFetch = React.useCallback(async (page, limit) => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      const formattedFromDate = new Date(fromDate).toISOString().split("T")[0];
      const formattedToDate = new Date(toDate).toISOString().split("T")[0];

      let filterBranchId = selectedBranch;
      if (!filterBranchId && shouldFilterByBranch) filterBranchId = userBranchId;

      const params = {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        page,
        limit,
      };

      if (filterBranchId) params.branchId = filterBranchId;

      if (selectedFieldOfficer) {
        const selectedStaff = fieldOfficers.find(s => String(s.id) === String(selectedFieldOfficer));
        const roleId = selectedStaff ? Number(selectedStaff.roleId) : null;
        if (roleId === 3) {
          params.fieldOfficerId = selectedFieldOfficer;
          params.managerId = selectedFieldOfficer;
        } else {
          params.managerId = selectedFieldOfficer;
        }
      }

      const response = await axios.get(`${API_BASE_URL}/api/getReportsOptimized`, { params });

      const transactionsData = response.data.data || [];
      const total = response.data.total || 0;

      setTransactions(transactionsData);
      setTotalCount(total);

      // Page-level totals (only for current page slice)
      const totalDep = transactionsData.filter(tx => tx.transactionType === 'deposit').reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      const totalWith = transactionsData.filter(tx => tx.transactionType === 'withdraw').reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
      setTotalDeposits(totalDep);
      setTotalWithdrawals(totalWith);

    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setTotalCount(0);
      setTotalDeposits(0);
      setTotalWithdrawals(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedBranch, selectedFieldOfficer, selectedBranchManager, fieldOfficers]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
    doFetch(1, limitRef.current);
  }, [fromDate, toDate, selectedBranch, selectedFieldOfficer, selectedBranchManager, fieldOfficers]);

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar (No enclosing border card) */}
      <div className="flex flex-row flex-wrap items-end justify-between gap-4 mb-3 py-1">
        <div className="flex flex-row flex-wrap items-center gap-3 flex-grow">
          {/* From Date */}
          <div className="w-[145px] mt-1">
            <DatePicker
              label="From Date"
              selected={fromDate}
              onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
              placeholder="DD-MM-YYYY"
            />
          </div>

          {/* To Date */}
          <div className="w-[145px] mt-1">
            <DatePicker
              label="To Date"
              selected={toDate}
              onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
              placeholder="DD-MM-YYYY"
            />
          </div>

          {/* Branch - Only visible for superadmin */}
          {isSuperAdmin && (
            <div className="relative min-w-[170px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Branch
              </label>
              <div className="relative">
                <select
                  value={selectedBranch}
                  onChange={handleBranchChange}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Staff Officer */}
          <div className="relative min-w-[170px] mt-1">
            <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
              Staff
            </label>
            <div className="relative">
              <select
                value={selectedFieldOfficer}
                onChange={handleFieldOfficerChange}
                className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
              >
                <option value="">Select Staff</option>
                {fieldOfficers.map((officer) => (
                  <option key={officer.id} value={officer.id}>{officer.name || officer.username}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Export to Excel Button on far right */}
        <Button
          onClick={exportToExcel}
          variant="primary"
          size="sm"
          className="shadow-sm h-[38px] px-4 font-bold"
        >
          <i className="fa-solid fa-file-excel mr-1.5"></i>
          Export to Excel
        </Button>
      </div>

      {loading && <Loader message="Loading report data..." />}

      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'sno' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'sno' ? null : 'sno')}
              >
                S.No
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'date' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'date' ? null : 'date')}
              >
                Date
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'id' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'id' ? null : 'id')}
              >
                Customer ID
              </Table.HeadCell>
              <Table.HeadCell
                align="left"
                sortable
                sortDirection={selectedColumn === 'name' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'name' ? null : 'name')}
              >
                Customer Name
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'acc' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'acc' ? null : 'acc')}
              >
                Account Number
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'type' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'type' ? null : 'type')}
              >
                Transaction Type
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'txid' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'txid' ? null : 'txid')}
              >
                Transaction Id
              </Table.HeadCell>
              <Table.HeadCell
                align="right"
                sortable
                sortDirection={selectedColumn === 'amount' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'amount' ? null : 'amount')}
              >
                Amount
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'narration' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'narration' ? null : 'narration')}
              >
                Narration
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'cashtype' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'cashtype' ? null : 'cashtype')}
              >
                CashType
              </Table.HeadCell>
              <Table.HeadCell
                align="center"
                sortable
                sortDirection={selectedColumn === 'time' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'time' ? null : 'time')}
              >
                Time
              </Table.HeadCell>
              <Table.HeadCell
                align="right"
                sortable
                sortDirection={selectedColumn === 'closing' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'closing' ? null : 'closing')}
              >
                Closing Balance
              </Table.HeadCell>
              <Table.HeadCell
                align="left"
                sortable
                sortDirection={selectedColumn === 'staff' ? 'desc' : undefined}
                onSort={() => setSelectedColumn(selectedColumn === 'staff' ? null : 'staff')}
              >
                Staff Name
              </Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {currentRows.length > 0 ? (
              currentRows.map((tx, index) => (
                <Table.Row key={tx.id}>
                  <Table.Cell align="center">{indexOfFirstRow + index + 1}</Table.Cell>
                  <Table.Cell align="center">{formatDate(tx.date)}</Table.Cell>
                  <Table.Cell align="center" className="font-medium">{tx.customerUniqueId}</Table.Cell>
                  <Table.Cell align="left">{tx.customerName}</Table.Cell>
                  <Table.Cell align="center">{tx.accountNumber}</Table.Cell>
                  <Table.Cell align="center" className="capitalize">{tx.transactionType}</Table.Cell>
                  <Table.Cell align="center">{tx.transactionId}</Table.Cell>
                  <Table.AmountCell align="right">
                    {parseFloat(tx.amount || 0)}
                  </Table.AmountCell>
                  <Table.Cell align="center">{tx.narration}</Table.Cell>
                  <Table.Cell align="center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${tx.cash_type === 'cash'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-indigo-100 text-indigo-800'
                      }`}>
                      {tx.cash_type}
                    </span>
                  </Table.Cell>
                  <Table.Cell align="center" className="text-xs text-ink-500">
                    {tx.createdAt ? new Date(tx.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : 'N/A'}
                  </Table.Cell>
                  <Table.AmountCell align="right" className="text-[#0369a1]">
                    {parseFloat(tx.closingBalance || 0)}
                  </Table.AmountCell>
                  <Table.Cell align="left">
                    <span className="bg-[#e8eaf6] text-[#283593] rounded px-2 py-0.5 text-xs font-semibold">
                      {tx.staffName || 'N/A'}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.EmptyRow colSpan={13} message="No transactions found" />
            )}
          </Table.Body>
        </Table>

        {/* Combined Footer: Pagination on left, Summary Totals on right */}
        <div className="flex flex-row flex-wrap items-center justify-between gap-4 mt-3">
          {/* Pagination Controls with standalone border box */}
          <div className="flex items-center gap-3 bg-white px-3 py-2 rounded-xl border border-border-light shadow-sm">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700 text-xs"
              >
                {[25, 50, 100, 200].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCurrentPage(1); doFetch(1, limitRef.current); }}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { const p = currentPage - 1; setCurrentPage(p); doFetch(p, limitRef.current); }}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { const p = currentPage + 1; setCurrentPage(p); doFetch(p, limitRef.current); }}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { const p = currentPage + 1; setCurrentPage(p); doFetch(p, limitRef.current); }}
                disabled={currentPage >= totalPages}
              >
                Last
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="flex items-center gap-3">
            <div className="bg-white px-3.5 py-1.5 rounded-xl border border-border-light shadow-sm flex flex-col gap-0.5">
              <span className="text-[9px] font-bold text-ink-400 uppercase tracking-wider">TOTAL DEPOSITS</span>
              <span className="text-base font-bold text-emerald-600">
                ₹{totalDeposits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="bg-white px-3.5 py-1.5 rounded-xl border border-border-light shadow-sm flex flex-col gap-0.5">
              <span className="text-[9px] font-bold text-ink-400 uppercase tracking-wider">TOTAL WITHDRAWALS</span>
              <span className="text-base font-bold text-rose-600">
                ₹{totalWithdrawals.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Reports;